import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { ArrowLeft, Settings, Wand2, X, MessageSquare } from 'lucide-react';
import { AIStyleTuner } from './AIStyleTuner';
import { collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { ManualAssessmentDraft, ManualEntry } from './ManualEntry';
import { AIGenerator } from './AIGenerator';
import { ExamPaper, Question as AIQuestion, SubQuestion as AISubQuestion } from './types';
import { AIChatAssistant } from './AIChatAssistant';

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { db, auth } from '@/firebase/config';
import { useToast } from '@/hooks/use-toast';

type AssessmentType = 'CAT' | 'Assignment' | 'Exam' | 'Quiz' | 'Custom';
type LegacyQuestionType = 'Multiple Choice' | 'Short Answer' | 'Essay' | 'True/False' | 'Section';

interface LegacySubQuestion {
  id: string;
  type: LegacyQuestionType;
  text: string;
  marks: number;
  options?: string[] | null;
  correctAnswerIndex?: number;
  modelAnswer?: string | null;
  image?: string | null;
  diagram?: Record<string, any> | null;
  diagramSpec?: Record<string, any> | null;
  diagrams?: Record<string, any>[];
}

interface LegacyQuestion {
  id: string;
  type: LegacyQuestionType;
  text: string;
  marks: number;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  options?: string[] | null;
  correctAnswerIndex?: number;
  modelAnswer?: string | null;
  learningObjective?: string;
  image?: string | null;
  diagram?: Record<string, any> | null;
  diagramSpec?: Record<string, any> | null;
  diagrams?: Record<string, any>[];
  subQuestions?: LegacySubQuestion[] | null;
  section?: string;
}

interface Beta2AIMeta {
  examTitle: string;
  examType: string;
  customExamName: string;
  instructions: string;
}

interface Beta2Source {
  manualDraft?: ManualAssessmentDraft | null;
  examPaper?: ExamPaper | null;
  aiMeta?: Beta2AIMeta | null;
}

interface AssessmentCreatorProps {
  onBack?: () => void;
  assessmentId?: string | null;
  initialAssessment?: any | null;
  onSaveSuccess?: () => void;
}

const createLegacyQuestionId = () => Math.random().toString(36).substr(2, 9);

const normalizeExamType = (value?: string): string => {
  if (value === 'Exam') return 'MAIN EXAM';
  if (value === 'Custom') return 'CUSTOM';
  return value || 'CAT';
};

const mapExamTypeToAssessmentType = (value?: string): AssessmentType => {
  if (value === 'MAIN EXAM') return 'Exam';
  if (value === 'CUSTOM') return 'Custom';
  if (value === 'Assignment' || value === 'Exam' || value === 'Quiz' || value === 'Custom') return value;
  return 'CAT';
};

const mapAITypeToLegacyType = (type?: string): LegacyQuestionType => {
  switch (type) {
    case 'multiple-choice':
      return 'Multiple Choice';
    case 'essay':
      return 'Essay';
    case 'short-answer':
    default:
      return 'Short Answer';
  }
};

const normalizeOptionText = (value?: string | null) =>
  (value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const resolveCorrectAnswerIndex = (options?: string[] | null, answer?: string | null) => {
  const normalizedOptions = (options || []).map((option) => option ?? '');
  if (normalizedOptions.length === 0 || !answer?.trim()) {
    return undefined;
  }

  const normalizedAnswer = normalizeOptionText(answer);
  const exactMatchIndex = normalizedOptions.findIndex((option) => normalizeOptionText(option) === normalizedAnswer);
  if (exactMatchIndex >= 0) {
    return exactMatchIndex;
  }

  const letterMatch = answer.match(/^\s*(?:option|choice|answer)?\s*\(?([A-Z])\)?(?:[\s\.\):-]|$)/i);
  if (letterMatch?.[1]) {
    const index = letterMatch[1].toUpperCase().charCodeAt(0) - 65;
    if (index >= 0 && index < normalizedOptions.length) {
      return index;
    }
  }

  const containingOptionIndex = normalizedOptions.findIndex((option) => {
    const normalizedOption = normalizeOptionText(option);
    return normalizedOption.length > 0 && normalizedAnswer.includes(normalizedOption);
  });
  if (containingOptionIndex >= 0) {
    return containingOptionIndex;
  }

  return undefined;
};

const normalizeOptions = (options?: string[] | null) => {
  const cleaned = (options || []).map((option) => option?.trim() || '').filter(Boolean);
  return cleaned.length > 0 ? cleaned : null;
};

const sanitizeForFirestore = (value: any): any => {
  if (value === undefined) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeForFirestore(item))
      .filter((item) => item !== undefined);
  }

  if (value && typeof value === 'object' && value.constructor === Object) {
    return Object.entries(value).reduce<Record<string, any>>((acc, [key, entry]) => {
      const sanitizedEntry = sanitizeForFirestore(entry);
      if (sanitizedEntry !== undefined) {
        acc[key] = sanitizedEntry;
      }
      return acc;
    }, {});
  }

  return value;
};

const buildModelAnswer = (answer?: string, explanation?: string[]) => {
  const normalizedAnswer = answer?.trim();
  const normalizedSteps = (explanation || []).map((item) => item?.trim()).filter(Boolean);

  const parts: string[] = [];
  if (normalizedAnswer) {
    parts.push(`Answer: ${normalizedAnswer}`);
  }

  normalizedSteps.forEach((step, index) => {
    parts.push(/^Step\s+\d+:/i.test(step) ? step : `Step ${index + 1}: ${step}`);
  });

  return parts.length > 0 ? parts.join('\n\n') : null;
};

const parseModelAnswer = (modelAnswer?: string | null) => {
  if (!modelAnswer) return { answer: '', explanation: [] as string[] };
  const parts = modelAnswer
    .split(/\n{2,}/)
    .map((item) => item.trim())
    .filter(Boolean);

  const firstPart = parts[0] || '';
  const answer = firstPart.replace(/^Answer:\s*/i, '').trim();
  return {
    answer,
    explanation: parts.slice(1),
  };
};

const convertAISubQuestion = (question: AISubQuestion): LegacySubQuestion => ({
  id: question.id || createLegacyQuestionId(),
  type: mapAITypeToLegacyType(question.type),
  text: question.text || '',
  marks: Number(question.marks) || 0,
  options: normalizeOptions(question.options),
  correctAnswerIndex: resolveCorrectAnswerIndex(normalizeOptions(question.options), question.answer),
  modelAnswer: buildModelAnswer(question.answer, question.explanation),
  image: question.image || null,
  diagram: question.diagram || null,
  diagramSpec: question.diagramSpec || null,
  diagrams: question.diagrams,
});

const convertAIQuestion = (question: AIQuestion, sectionName: string): LegacyQuestion => ({
  id: question.id || createLegacyQuestionId(),
  type: mapAITypeToLegacyType(question.type),
  text: question.text || '',
  marks: Number(question.marks) || 0,
  difficulty: question.difficulty || 'Medium',
  options: normalizeOptions(question.options),
  correctAnswerIndex: resolveCorrectAnswerIndex(normalizeOptions(question.options), question.answer),
  modelAnswer: buildModelAnswer(question.answer, question.explanation),
  image: question.image || null,
  diagram: question.diagram || null,
  diagramSpec: question.diagramSpec || null,
  diagrams: question.diagrams,
  subQuestions: question.subQuestions?.map(convertAISubQuestion) || null,
  section: sectionName,
});

const examPaperToLegacyAssessment = (paper: ExamPaper, meta?: Beta2AIMeta, existing?: any) => {
  const questions = paper.sections.flatMap((section) => section.questions.map((question) => convertAIQuestion(question, section.name)));
  const totalMarks = questions.reduce((sum, question) => {
    const subMarks = question.subQuestions?.reduce((subTotal, subQuestion) => subTotal + (Number(subQuestion.marks) || 0), 0) || 0;
    return sum + (question.subQuestions && question.subQuestions.length > 0 ? subMarks : Number(question.marks) || 0);
  }, 0);

  return {
    title: (meta?.examType === 'CUSTOM' ? meta?.customExamName : meta?.examTitle) || paper.title || existing?.title || 'Assessment Draft',
    type: mapExamTypeToAssessmentType(meta?.examType || existing?.type),
    instructions: meta?.instructions || existing?.instructions || 'Answer all questions in the spaces provided.',
    structure: paper.sections.map((section) => ({ name: section.name, count: section.questions.length })),
    questions,
    totalMarks,
    duration: existing?.duration || '40 Mins',
  };
};

const legacyAssessmentToExamPaper = (assessment: any): ExamPaper => {
  const questionGroups = new Map<string, LegacyQuestion[]>();
  const assessmentQuestions = (assessment?.questions || []) as LegacyQuestion[];

  assessmentQuestions.forEach((question) => {
    const sectionName = question.section || 'Section A';
    if (!questionGroups.has(sectionName)) {
      questionGroups.set(sectionName, []);
    }
    questionGroups.get(sectionName)!.push(question);
  });

  const sections = Array.from(questionGroups.entries()).map(([sectionName, questions]) => ({
    name: sectionName,
    instructions: assessment?.instructions || 'Answer all questions in the spaces provided.',
    questions: questions.map((question) => {
      const parsed = parseModelAnswer(question.modelAnswer);
      return {
        id: question.id,
        text: question.text,
        type: (question.type === 'Multiple Choice' ? 'multiple-choice' : question.type === 'Essay' ? 'essay' : 'short-answer') as "multiple-choice" | "short-answer" | "essay",
        marks: Number(question.marks) || 0,
        difficulty: question.difficulty || 'Medium',
        options: question.options || undefined,
        answer: question.options && question.correctAnswerIndex !== undefined ? question.options[question.correctAnswerIndex] || '' : parsed.answer,
        explanation: parsed.explanation,
        image: question.image || null,
        diagram: question.diagram || null,
        diagramSpec: question.diagramSpec || null,
        diagrams: question.diagrams,
        subQuestions: question.subQuestions?.map((subQuestion) => {
          const parsedSub = parseModelAnswer(subQuestion.modelAnswer);
          return {
            id: subQuestion.id,
            text: subQuestion.text,
            type: (subQuestion.type === 'Multiple Choice' ? 'multiple-choice' : subQuestion.type === 'Essay' ? 'essay' : 'short-answer') as "multiple-choice" | "short-answer" | "essay",
            marks: Number(subQuestion.marks) || 0,
            options: subQuestion.options || undefined,
            answer: subQuestion.options && subQuestion.correctAnswerIndex !== undefined ? subQuestion.options[subQuestion.correctAnswerIndex] || '' : parsedSub.answer,
            explanation: parsedSub.explanation,
            image: subQuestion.image || null,
            diagram: subQuestion.diagram || null,
            diagramSpec: subQuestion.diagramSpec || null,
            diagrams: subQuestion.diagrams,
          };
        }) || undefined,
      };
    }),
  }));

  return {
    title: assessment?.title || 'Assessment Draft',
    sections: sections.length > 0 ? sections : [{ name: 'Section A', instructions: assessment?.instructions || 'Answer all questions in the spaces provided.', questions: [] }],
  };
};

const createManualDraftFromAssessment = (assessment?: any | null): ManualAssessmentDraft => ({
  title: assessment?.title || 'CAT Assessment',
  type: mapExamTypeToAssessmentType(assessment?.type),
  examType: normalizeExamType(assessment?.type),
  customExamName: assessment?.type === 'Custom' ? assessment?.title || '' : '',
  topics: [],
  instructions: assessment?.instructions || assessment?.description || 'Answer all questions in the spaces provided.',
  duration: assessment?.duration || '40 Mins',
  questions: assessment?.questions || [],
  sections: (assessment?.structure || [{ name: 'Section A', count: Math.max((assessment?.questions || []).length, 1) }]).map((section: any, index: number) => ({
    id: index + 1,
    name: section.name,
    qnCount: Number(section.count) || 0,
  })),
  structure: assessment?.structure || [{ name: 'Section A', count: Math.max((assessment?.questions || []).length, 1) }],
  totalMarks: assessment?.totalMarks || 0,
});

const normalizeSource = (assessment?: any | null): Beta2Source => {
  if (assessment?.beta2Source) {
    return assessment.beta2Source as Beta2Source;
  }

  return {
    manualDraft: assessment ? createManualDraftFromAssessment(assessment) : null,
    examPaper: assessment?.creatorVersion === 'beta2' && assessment?.beta2Mode === 'ai' ? legacyAssessmentToExamPaper(assessment) : null,
    aiMeta: assessment ? {
      examTitle: assessment.title || 'Midterm Exam',
      examType: normalizeExamType(assessment.type),
      customExamName: assessment.type === 'Custom' ? assessment.title || '' : '',
      instructions: assessment.instructions || '',
    } : null,
  };
};

export function AssessmentCreator({ onBack, assessmentId, initialAssessment, onSaveSuccess }: AssessmentCreatorProps) {
  const { toast } = useToast();
  const initialSource = useMemo(() => normalizeSource(initialAssessment), [initialAssessment]);

  const [mode, setMode] = useState<'manual' | 'ai'>(initialAssessment?.beta2Mode === 'ai' ? 'ai' : 'manual');
  const [examPaper, setExamPaper] = useState<ExamPaper | null>(initialSource.examPaper || null);
  const [manualDraft, setManualDraft] = useState<ManualAssessmentDraft | null>(initialSource.manualDraft || createManualDraftFromAssessment(initialAssessment));
  const [aiMeta, setAiMeta] = useState<Beta2AIMeta>(initialSource.aiMeta || {
    examTitle: initialAssessment?.title || 'Midterm Exam',
    examType: normalizeExamType(initialAssessment?.type),
    customExamName: initialAssessment?.type === 'Custom' ? initialAssessment?.title || '' : '',
    instructions: initialAssessment?.instructions || '',
  });
  const [assessmentTitle, setAssessmentTitle] = useState(initialAssessment?.title || initialSource.examPaper?.title || initialSource.aiMeta?.customExamName || initialSource.aiMeta?.examTitle || initialSource.manualDraft?.title || 'Assessment Draft');
  const [saving, setSaving] = useState(false);
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [tempName, setTempName] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [showTuner, setShowTuner] = useState(false);
  const [showChat, setShowChat] = useState(false);

  const [backVisible, setBackVisible] = useState(true);
  const backTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showBack = useCallback(() => {
    setBackVisible(true);
    if (backTimer.current !== null) clearTimeout(backTimer.current);
    backTimer.current = setTimeout(() => setBackVisible(false), 5000);
  }, []);

  useEffect(() => {
    const events: Array<keyof DocumentEventMap> = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => document.addEventListener(e, showBack));
    showBack();
    return () => {
      events.forEach(e => document.removeEventListener(e, showBack));
      if (backTimer.current !== null) clearTimeout(backTimer.current);
    };
  }, [showBack]);

  useEffect(() => {
    const source = normalizeSource(initialAssessment);
    setMode(initialAssessment?.beta2Mode === 'ai' ? 'ai' : 'manual');
    setExamPaper(source.examPaper || null);
    setManualDraft(source.manualDraft || createManualDraftFromAssessment(initialAssessment));
    setAiMeta(source.aiMeta || {
      examTitle: initialAssessment?.title || 'Midterm Exam',
      examType: normalizeExamType(initialAssessment?.type),
      customExamName: initialAssessment?.type === 'Custom' ? initialAssessment?.title || '' : '',
      instructions: initialAssessment?.instructions || '',
    });
    setAssessmentTitle(initialAssessment?.title || source.examPaper?.title || source.aiMeta?.customExamName || source.aiMeta?.examTitle || source.manualDraft?.title || 'Assessment Draft');
  }, [initialAssessment]);

  const handleSave = async (titleOverride?: string) => {
    if (!auth.currentUser) {
      toast({
        title: 'Authentication required',
        description: 'You must be logged in to save an assessment.',
        variant: 'destructive',
      });
      return;
    }

    const payload = mode === 'ai'
      ? (examPaper ? examPaperToLegacyAssessment(examPaper, aiMeta, initialAssessment) : null)
      : manualDraft;

    if (!payload) {
      toast({
        title: 'Nothing to save',
        description: mode === 'ai' ? 'Generate or edit an assessment first.' : 'Add assessment details before saving.',
        variant: 'destructive',
      });
      return;
    }

    const finalTitle = titleOverride ?? assessmentTitle;
    payload.title = finalTitle;
    setAssessmentTitle(finalTitle);

    if (!payload.title?.trim()) {
      toast({
        title: 'Title required',
        description: 'Provide a valid assessment title before saving.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const baseData = sanitizeForFirestore({
        title: payload.title,
        type: payload.type,
        instructions: payload.instructions,
        structure: payload.structure,
        questions: payload.questions,
        totalMarks: payload.totalMarks,
        duration: payload.duration,
        status: initialAssessment?.status || 'Draft',
        creatorVersion: 'beta2',
        beta2Mode: mode,
        beta2Source: {
          manualDraft: mode === 'manual' ? payload : null,
          examPaper: mode === 'ai' ? examPaper : null,
          aiMeta: mode === 'ai' ? aiMeta : null,
        },
        updatedAt: serverTimestamp(),
      });

      if (assessmentId) {
        await setDoc(doc(db, `teaching_workspaces/${auth.currentUser.uid}/assessments/${assessmentId}`), baseData, { merge: true });
      } else {
        const newRef = doc(collection(db, `teaching_workspaces/${auth.currentUser.uid}/assessments`));
        await setDoc(newRef, sanitizeForFirestore({
          ...baseData,
          createdAt: serverTimestamp(),
        }));
      }

      toast({
        title: 'Assessment saved',
        description: 'Beta 2 assessment is now fully available in the dashboard flow.',
      });
      onSaveSuccess?.();
      onBack?.();
    } catch (error) {
      console.error('Error saving Beta 2 assessment:', error);
      toast({
        title: 'Save failed',
        description: 'Unable to save the assessment. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col px-0 animate-in fade-in slide-in-from-top-2 duration-500 pb-10">
      <div className="sticky top-0 z-20 flex items-center justify-between -mx-0">
        <button
          onClick={onBack}
          title="Go back"
          aria-label="Go back"
          className={`h-7 w-7 flex items-center justify-center rounded-full bg-zinc-100 dark:bg-white/5 text-zinc-500 hover:text-zinc-900 dark:hover:text-white border border-zinc-200 dark:border-white/10 transition-all duration-300 ${
            backVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75 pointer-events-none'
          }`}
        >
          <ArrowLeft className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => setShowSettings(true)}
          title="Settings"
          aria-label="Settings"
          className="h-7 w-7 flex items-center justify-center rounded-full bg-zinc-100 dark:bg-white/5 text-zinc-500 hover:text-zinc-900 dark:hover:text-white border border-zinc-200 dark:border-white/10 transition-all duration-300"
        >
          <Settings className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-8 print:hidden">
         <div className="min-w-0 flex-1 w-full">
          <div className="flex flex-wrap items-center gap-3">
             <input 
                 type="text" 
                 value={assessmentTitle}
                 onChange={(e) => setAssessmentTitle(e.target.value)}
                 placeholder="Enter Assessment Title..."
                 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900 dark:text-gray-100 bg-transparent border-b border-transparent focus:border-gray-300 dark:focus:border-gray-700 focus:outline-none p-0 placeholder-gray-400 w-full sm:w-auto sm:min-w-[300px]"
             />
             <Badge variant="outline" className="text-primary border-primary/30 dark:border-primary/20 shrink-0">Advanced Mode</Badge>
          </div>
          <p className="text-muted-foreground mt-1">Design learner-centered assessments aligned with learning outcomes.</p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <Button variant="outline" onClick={onBack} className="rounded-xl font-bold border-gray-200 dark:border-white/10 text-muted-foreground hover:bg-red-50 hover:text-red-600 hover:border-red-100 transition-all flex-1 sm:flex-none">
               Discard
            </Button>
            <Button 
                onClick={() => {
                  setTempName(assessmentTitle);
                  setShowNamePrompt(true);
                }}
                disabled={saving}
                className="rounded-xl font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20 flex-1 sm:flex-none"
            >
               {saving ? 'Saving...' : 'Save Assessment'}
            </Button>
        </div>
      </div>

      <div className="space-y-6">
        {mode === 'manual' ? (
          <ManualEntry 
            key={`manual-${assessmentId || 'new'}-${initialAssessment?.updatedAt?.seconds || 'draft'}`}
            mode={mode} 
            setMode={setMode} 
            examPaper={examPaper} 
            setExamPaper={setExamPaper} 
            onBack={() => {}} 
            initialData={manualDraft}
            onDraftChange={setManualDraft}
          />
        ) : (
          <AIGenerator 
            key={`ai-${assessmentId || 'new'}-${initialAssessment?.updatedAt?.seconds || 'draft'}`}
            mode={mode} 
            setMode={setMode} 
            examPaper={examPaper} 
            setExamPaper={setExamPaper} 
            onBack={() => {}} 
            initialMeta={aiMeta}
            onMetaChange={setAiMeta}
            onSaveDraft={() => {
              setTempName(assessmentTitle);
              setShowNamePrompt(true);
            }}
            saving={saving}
            onOpenChat={() => setShowChat(true)}
          />
        )}
      </div>

      {showNamePrompt && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-xl w-full max-w-sm p-6 animate-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Name Assessment</h3>
            <p className="text-sm text-gray-500 mb-4">Provide a clear name to easily identify this assessment later.</p>
            <input 
              type="text" 
              value={tempName}
              onChange={(e) => setTempName(e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-gray-300 dark:border-zinc-700 bg-transparent text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-6"
              placeholder="e.g. Midterm Math Exam"
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowNamePrompt(false)} className="rounded-lg">Cancel</Button>
              <Button onClick={() => { setShowNamePrompt(false); handleSave(tempName); }} className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white">Save</Button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Panel */}
      {showSettings && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-xs overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 dark:border-white/10">
              <span className="text-sm font-bold text-gray-900 dark:text-gray-100">Settings</span>
              <button onClick={() => setShowSettings(false)} className="h-7 w-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-3 space-y-2">
              <button
                onClick={() => { setShowSettings(false); setShowTuner(true); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors text-left group"
              >
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-md shadow-blue-500/20 shrink-0">
                  <Wand2 className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 transition-colors">AI Style Tuner</p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">Train the generator to match your exam style</p>
                </div>
              </button>

              <button
                onClick={() => { setShowSettings(false); setShowChat(true); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors text-left group"
              >
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md shadow-blue-500/20 shrink-0">
                  <MessageSquare className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 transition-colors">AI Chat Assistant</p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">Chat with AI for ideas and review</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Style Tuner */}
      {showTuner && <AIStyleTuner onClose={() => setShowTuner(false)} />}

      {/* Floating AI Chat Assistant */}
      {showChat && (
        <AIChatAssistant 
          onClose={() => setShowChat(false)} 
          context={assessmentTitle} 
          examPaper={examPaper}
          onUpdateExamPaper={setExamPaper}
        />
      )}
    </div>
  );
}
